import React, { useMemo } from 'react';
import { TimeSeriesLineChart } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { TimePhasedUnitsPoint } from '@/utils/buildTimePhasedUnitsSeries';
import type { EChartsOption } from 'echarts';

export interface PlannedUnitsSCurveChartProps {
  points: TimePhasedUnitsPoint[];
  height?: number | string;
  showDataLabels: boolean;
  density?: ChartDensity;
}

/** Cumulative planned units over the schedule (same time-phased spread as the units/day chart). */
export function PlannedUnitsSCurveChart({
  points,
  height = 260,
  showDataLabels = true,
  density = 'overview',
}: PlannedUnitsSCurveChartProps) {
  const xAxisData = useMemo(() => points.map((p) => p.date), [points]);
  const cumulative = useMemo(() => points.map((p) => p.cumulativeUnits), [points]);

  const optionOverrides = useMemo<Partial<EChartsOption>>(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const arr = params as Array<{ axisValue?: string; value?: number | string }>;
          const p0 = arr[0];
          if (!p0) return '';
          const d = String(p0.axisValue ?? '');
          const v = typeof p0.value === 'number' ? p0.value.toFixed(2) : String(p0.value ?? '');
          return `${d}<br/>Cumulative units: ${v}`;
        },
      },
    }),
    []
  );

  return (
    <TimeSeriesLineChart
      xAxisData={xAxisData}
      series={[{ name: 'Cumulative planned units', data: cumulative, type: 'line', smooth: false }]}
      height={height}
      showDataLabels={showDataLabels}
      density={density}
      optionOverrides={optionOverrides}
    />
  );
}
